import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, formatCurrency } from '@/lib/utils';
import { INVOICE_LIFECYCLE_LABELS } from '@jobos/shared';
import type { InvoiceKind, InvoiceLifecycle } from '@jobos/shared';
import { formatBillingDate } from './billingDate';
import type { BillingInvoiceRow } from './invoiceRow';

/** The small pieces the table and the cards both render, so they cannot drift. */

const LIFECYCLE_STYLES: Record<InvoiceLifecycle, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-yellow-100 text-yellow-800',
  partially_paid: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  voided: 'bg-gray-100 text-gray-600',
};

export function LifecycleBadge({ lifecycle }: { lifecycle: InvoiceLifecycle }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium',
        LIFECYCLE_STYLES[lifecycle],
      )}
    >
      {INVOICE_LIFECYCLE_LABELS[lifecycle]}
    </span>
  );
}

export function KindBadge({ kind, isCash }: { kind: InvoiceKind; isCash?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium',
        kind === 'final' ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800',
      )}
    >
      {/* A cash final says so too. It reads as "Final" everywhere else on the
          row, and the C in the number (…-C1) is the only other tell. */}
      {kind === 'final' ? (isCash ? 'Final · Cash' : 'Final') : isCash ? 'Cash' : 'Partial'}
    </span>
  );
}

/**
 * The amount, or an honest admission that we do not know it.
 *
 * A backfilled QuickBooks receivable can carry amount 0 because the legacy job
 * had no breakdown total. Rendering that as "$0.00" states something false
 * about a debt someone is owed, so the row says so and offers the amend flow
 * instead (spec §5).
 */
export function InvoiceAmount({
  row,
  onAmend,
  className,
}: {
  row: Pick<BillingInvoiceRow, 'amount' | 'amountUnknown'>;
  onAmend?: () => void;
  className?: string;
}) {
  if (!row.amountUnknown) {
    return <span className={cn('whitespace-nowrap tabular-nums', className)}>{formatCurrency(row.amount)}</span>;
  }

  const label = 'Amount not recorded';
  return onAmend ? (
    <button
      type="button"
      onClick={onAmend}
      className={cn(
        'whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium text-amber-800',
        'bg-amber-100 hover:bg-amber-200',
        className,
      )}
    >
      {label}
    </button>
  ) : (
    <span
      className={cn(
        'whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800',
        className,
      )}
    >
      {label}
    </span>
  );
}

/** Outstanding is what is owed today — the figure a receivables screen exists to show. */
export function OutstandingAmount({ row }: { row: Pick<BillingInvoiceRow, 'outstanding' | 'amountUnknown'> }) {
  if (row.amountUnknown) return <span className="text-gray-400">—</span>;
  return (
    <span className={cn('whitespace-nowrap tabular-nums', row.outstanding > 0 ? 'text-red-600' : 'text-gray-500')}>
      {formatCurrency(row.outstanding)}
    </span>
  );
}

/**
 * When the invoice says payment is due.
 *
 * An em dash, not a guess, when the invoice states no terms — every backfilled
 * QuickBooks-era row is one, and those age by the `overdue_after_days` fallback
 * instead. Printing a date there would claim the customer was told something
 * they were not.
 *
 * Red is driven by `isOverdue` from the server, never by comparing the date
 * here: the server owns that rule (spec D6), and on a row with no terms it is
 * answering a different question than this cell is showing.
 */
export function DueCell({
  row,
}: {
  row: Pick<BillingInvoiceRow, 'dueDate' | 'isOverdue'>;
}) {
  if (!row.dueDate) return <span className="text-gray-400">—</span>;
  return (
    <span className={row.isOverdue ? 'font-medium text-red-600' : undefined}>
      {formatBillingDate(row.dueDate)}
    </span>
  );
}

/**
 * When the invoice went out, and the one place to correct it.
 *
 * Read-only without `onSetSentDate` — a surface that cannot write the date
 * still shows it. With the handler it is a button opening a calendar, because
 * the case this exists for is an invoice that left by a channel this system
 * never saw: texted to the client, handed over on site. Nothing stamped it, so
 * somebody has to say which day it was.
 *
 * A day, not an instant: the popover sends `YYYY-MM-DD` and the server anchors
 * it inside that day. `formatBillingDate` renders a stamped send's true timestamp as
 * the same kind of date, so the two are indistinguishable in the column —
 * which is the point (spec D7).
 */
export function SentDateCell({
  row,
  onSetSentDate,
  className,
}: {
  row: Pick<BillingInvoiceRow, 'sentAt' | 'lifecycle'>;
  onSetSentDate?: (date: string | null) => void | Promise<void>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const label = row.sentAt ? formatBillingDate(row.sentAt) : '—';

  if (!onSetSentDate) {
    return (
      <span className={cn(row.sentAt ? undefined : 'text-gray-400', className)}>{label}</span>
    );
  }

  const commit = async (date: string | null) => {
    setOpen(false);
    await onSetSentDate(date);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={row.sentAt ? `Edit sent date (${label})` : 'Set sent date'}
          className={cn(
            'rounded px-1.5 py-0.5 text-left transition-colors hover:bg-gray-100',
            row.sentAt ? 'text-gray-700' : 'text-gray-400',
            className,
          )}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={row.sentAt ? new Date(row.sentAt) : undefined}
          defaultMonth={row.sentAt ? new Date(row.sentAt) : undefined}
          onSelect={(date) => {
            if (date) void commit(format(date, 'yyyy-MM-dd'));
          }}
          fixedWeeks
        />
        {row.sentAt && (
          <div className="border-t p-2">
            {/*
              Clearing is the undo for a mis-entered date, and the server
              refuses it once payments are recorded — a cleared date would read
              as a draft carrying money. Offered here anyway: the error says
              what to do about it, and hiding the button would make an invoice
              dated by mistake look uncorrectable.
            */}
            <button
              type="button"
              onClick={() => void commit(null)}
              className="w-full rounded px-2 py-1 text-left text-sm text-gray-600 hover:bg-gray-100"
            >
              Not sent yet
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
